import { AntDesign } from "@expo/vector-icons";
import { BlurView } from "expo-blur";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, Modal, Pressable, Text } from "react-native";
import { useDispatch } from "react-redux";

import Cryptotypes from "@/assets/cryptotypes.svg";
import Loader from "@/components/Loader";
import Subtitle from "@/components/Subtitle";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import InputChoose from "@/components/input/InputChoose";
import { setForm } from "@/context/actions/actions";
import { sendRequest } from "@/utils/ApiUtil";
import {
  AccountAssetsCryptoOptions,
  AccountAssetsFiatOptions,
  AccountTypeOptions,
  BlockchainCryptoOptions,
} from "@/utils/dto/extracted_enums";
import { blockchainAssets } from "@/utils/transactions/ExchangeCurrencies";

interface AddAccountModalProps {
  visible: boolean;
  onClick?: (state: boolean) => void;
  onClose: () => void;
  hasChanged?: (state: boolean) => void;
}

interface FormData {
  type: string;
  network: string;
  asset: string;
}

export default function AddAccountModal(props: AddAccountModalProps) {
  const [loading, setLoading] = useState(false);
  const [accountType, setAccountType] = useState("");
  const [assetType, setAssetType] = useState("");
  const [blockchainType, setBlockchainType] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [addAccount, setAddAccount] = useState(false);
  const [accountSuccess, setAccountSuccess] = useState(false);
  const [accountError, setAccountError] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        setLoading(true);
        const data = { type: accountType, asset: assetType };
        if (blockchainType !== "") {
          data["network"] = blockchainType;
        }
        const response = await sendRequest("post", "/accounts", data, true);
        if (response) {
          await sendRequest("get", "/accounts", null, true);
          setAddAccount(false);
          setAccountSuccess(true);
          setAccountType("");
          setAssetType("");
          setBlockchainType("");
          dispatch(setForm({ accountHasChanges: true }));
        }
      } catch (error: any) {
        console.log(error);
        setErrorMessage(error.data.message);
        setAddAccount(false);
        setAccountError(true);
        setAccountType("");
        setAssetType("");
        setBlockchainType("");
      } finally {
        setLoading(false);
      }
    };

    if (addAccount) {
      fetchAccount();
    }
  }, [addAccount]);

  useEffect(() => {
    if (accountSuccess || accountError) {
      setTimeout(() => {
        handleClose();
      }, 5000);
    }
  }, [accountSuccess, accountError]);

  const handleClose = () => {
    props.onClose();
    setAccountSuccess(false);
    setAccountError(false);
    setAssetType("");
    setAccountType("");
  };

  const { control, handleSubmit } = useForm<FormData>({
    mode: "onSubmit",
  });

  const onSubmit = async (data: FormData) => {
    setAddAccount(true);
  };

  function getFilteredCryptoOptions(
    blockchainType: keyof typeof blockchainAssets,
  ) {
    const assets = blockchainAssets[blockchainType];
    enum nullValues {
      default = "",
    }

    if (!assets) {
      return { nullValues };
    }

    const filteredOptions = Object.fromEntries(
      Object.entries(AccountAssetsCryptoOptions).filter(([key, value]) =>
        assets.includes(value),
      ),
    );

    return { filteredOptions };
  }

  return (
    <Modal visible={props.visible} transparent animationType="fade">
      <BlurView intensity={20} className="flex-1 justify-center items-center">
        <View className="p-6 bg-white1 border border-gray rounded-lg w-full h-full items-center relative shadow lg:w-104 lg:h-168">
          <Pressable className="absolute right-3 top-3" onPress={handleClose}>
            <AntDesign name="close" size={24} color="black" />
          </Pressable>
          <Cryptotypes width={140} height={140} />
          <View className="m-2">
            <Title text={t("common:modal.addaccount.title")} />
          </View>
          <Subtitle text={t("common:modal.addaccount.subtitle")} />
          {!accountSuccess && !accountError && (
            <>
              <View className="z-50 mt-4">
                <InputChoose
                  label={t("home/home:input.accounttype")}
                  name="accountType"
                  data={{ AccountTypeOptions }}
                  control={control}
                  handleChange={(value) => setAccountType(value)}
                />
              </View>
              <View className="z-40">
                <InputChoose
                  label={t("home/home:input.blockchain")}
                  name="network"
                  control={control}
                  blocked={accountType === "" || accountType === "FIAT"}
                  handleChange={(value) => setBlockchainType(value)}
                  data={{ BlockchainCryptoOptions }}
                />
              </View>
              <View className="z-30">
                <InputChoose
                  label={t("home/home:input.assettype")}
                  name="asset"
                  control={control}
                  blocked={accountType === ""}
                  handleChange={(value) => setAssetType(value)}
                  data={
                    accountType === "FIAT"
                      ? { AccountAssetsFiatOptions }
                      : getFilteredCryptoOptions(
                          blockchainType as keyof typeof blockchainAssets,
                        )
                  }
                />
              </View>
              <Button
                label={t("common:button.confirm")}
                onClick={handleSubmit(onSubmit)}
                blocked={
                  accountType === "" ||
                  assetType === "" ||
                  (accountType === "CRYPTO" && blockchainType === "")
                }
              />
            </>
          )}
          {accountSuccess && (
            <View className="flex-grow items-center justify-center">
              <AntDesign name="checkcircle" size={48} color="green" />
              <Text className="text-xl text-center m-4">
                {t("home/home:card.accountapproved")}
              </Text>
            </View>
          )}
          {accountError && (
            <View className="flex-grow items-center justify-center">
              <View className="m-2">
                <AntDesign name="closecircle" size={48} color="red" />
              </View>
              <Text className="text-xl text-center m-2">
                {t("home/home:card.accountrejected")}
              </Text>
              <Text className="text-xl text-center">{errorMessage}</Text>
            </View>
          )}
        </View>
        {loading && <Loader />}
      </BlurView>
    </Modal>
  );
}
